/**
 * A person's own User Preferences, shared by the API (which validates and
 * stores them on the account) and the client (which applies them).
 *
 * The set and its order follow the suite-wide User Preferences page: theme,
 * text size, typeface, clock, landing tab, behaviour, two-step verification,
 * dashboard and analytics arrangement; then Education's occasion greetings.
 * Every field defaults when absent, so an account saved before a field
 * existed reads as the default rather than as missing.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
/** Small, Medium, Large: base body text at 12, 14 and 16px, as across the suite. */
export type TextSizePreference = 'sm' | 'md' | 'lg'
export type FontPreference = 'standard' | 'system' | 'serif' | 'legible'

/** A user's arrangement of a page's panels: their order, and which are hidden. */
export interface PanelArrangement {
  order: string[]
  hidden: string[]
}

export interface UserPreferences {
  theme: ThemePreference
  textSize: TextSizePreference
  font: FontPreference
  clockFormat: '12' | '24'
  /** An in-app path; null means Home. */
  landingPage: string | null
  /** Keep the last few global searches and offer them when the box is empty. */
  rememberRecentSearches: boolean
  /** Newest first, at most RECENT_SEARCH_LIMIT. Always empty while remembering is off. */
  recentSearches: string[]
  /** Title-case name and place fields when they lose focus. */
  autoCapitalize: boolean
  /** The Report center's report tiles. */
  reportsLayout: PanelArrangement
  greetings: {
    /** MM/DD, or empty for "not set". */
    birthday: string
    showBirthday: boolean
    showHolidays: boolean
  }
}

export const RECENT_SEARCH_LIMIT = 5

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  theme: 'system',
  textSize: 'md',
  font: 'standard',
  clockFormat: '12',
  landingPage: null,
  rememberRecentSearches: true,
  recentSearches: [],
  autoCapitalize: true,
  reportsLayout: { order: [], hidden: [] },
  greetings: { birthday: '', showBirthday: true, showHolidays: true },
}

/**
 * A saved text size in today's three positions. Education's old fourth size,
 * Extra Large, becomes Large; a px number (11/12, 13/14, 16) lands on the
 * size in the same position; anything else is Medium.
 */
export function normalizeTextSize(value: unknown): TextSizePreference {
  if (value === 'sm' || value === 'md' || value === 'lg') return value
  if (value === 'xl') return 'lg'
  const px = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  if (px === 11 || px === 12) return 'sm'
  if (px >= 15) return 'lg'
  return 'md'
}

export function resolveUserPreferences(stored: Partial<UserPreferences> | null | undefined): UserPreferences {
  const merged: UserPreferences = {
    ...DEFAULT_USER_PREFERENCES,
    ...(stored ?? {}),
    reportsLayout: { ...DEFAULT_USER_PREFERENCES.reportsLayout, ...(stored?.reportsLayout ?? {}) },
    greetings: { ...DEFAULT_USER_PREFERENCES.greetings, ...(stored?.greetings ?? {}) },
  }
  merged.textSize = normalizeTextSize(merged.textSize)
  // Nothing is kept while remembering is off, whatever an older copy holds.
  merged.recentSearches = merged.rememberRecentSearches ? (merged.recentSearches ?? []).slice(0, RECENT_SEARCH_LIMIT) : []
  return merged
}

/** Adds a search to the front of the list: no duplicates (ignoring case), newest first, five at most. */
export function pushRecentSearch(list: readonly string[], term: string): string[] {
  const clean = term.trim().replace(/\s+/g, ' ')
  if (!clean) return [...list]
  const lower = clean.toLowerCase()
  return [clean, ...list.filter((item) => item.toLowerCase() !== lower)].slice(0, RECENT_SEARCH_LIMIT)
}

/**
 * The panel order to show: the saved order first, minus panels that no longer
 * exist, then any panel added since in its default place at the end. A saved
 * arrangement never hides a panel the user has not seen yet.
 */
export function mergePanelOrder(saved: readonly string[] | undefined, defaults: readonly string[]): string[] {
  const known = new Set(defaults)
  const kept = (saved ?? []).filter((id, index, all) => known.has(id) && all.indexOf(id) === index)
  return [...kept, ...defaults.filter((id) => !kept.includes(id))]
}

/**
 * Title Case for a name or a place, as typed: the first letter of each word
 * and of each part after a hyphen or an O'/D'-style apostrophe is raised.
 * A part that already has a capital in it is left exactly as it is, so
 * "McDonald", "DeShawn" and "LaToya" survive, and so does "STEM".
 */
export function titleCaseName(value: string): string {
  return value.replace(/[^\s\-'’]+|[\-'’]/g, (part, offset: number, whole: string) => {
    if (/^[\-'’]$/.test(part) || /\p{Lu}/u.test(part)) return part
    const before = whole.slice(0, offset)
    // After an apostrophe only a short prefix (O', D', L') starts a new capital: "o'neil" → "O'Neil", but "ma'am" stays.
    if (/['’]$/.test(before) && !/(^|[\s\-])\p{L}['’]$/u.test(before)) return part
    return part.charAt(0).toLocaleUpperCase() + part.slice(1)
  })
}

/**
 * Whether a field's label names a person or a place, the fields
 * auto-capitalization applies to. Deliberately a short list: "Name" alone
 * also labels courses, rooms and zones, where Title Case would be wrong.
 */
export function isNameLabel(label: string): boolean {
  const text = label.trim().toLowerCase().replace(/[*:]+$/, '').trim()
  if (!text) return false
  if (/e-?mail|user ?name|login|password|\bid\b|number|code|phone|note|comment/.test(text)) return false
  return /\b(first|last|middle|preferred|given|family|legal|maiden|nick|full|sur|student|guardian|parent|staff|contact|visitor|teacher|school|district|employer|agency)\s?name\b|\bsurname\b|\bcity\b|\btown\b|\bbirthplace\b|\bplace of birth\b|\bcity of birth\b/.test(
    text,
  )
}
